import { decryptJson, encryptJson } from '@/lib/crypto';
import { getDb } from './schema';

/**
 * Device-only store for source passwords the user chose to remember so a
 * form-login source (Picacg) can renew expired tokens by itself.
 *
 * Deliberately separate from `credentials`: rows live in the `meta` store,
 * which account sync never reads, and are encrypted with a non-extractable
 * AES-GCM key generated on this device. Neither the password nor the key ever
 * leaves the browser profile.
 */

export interface SourceLoginSecret {
  username: string;
  password: string;
}

const DEVICE_KEY = 'sourceLogin.deviceKey';
const secretKey = (sourceId: string) => `sourceLogin:${sourceId}`;

async function deviceKey(create: boolean): Promise<CryptoKey | undefined> {
  const db = await getDb();
  const row = await db.get('meta', DEVICE_KEY);
  if (row?.value instanceof CryptoKey) return row.value;
  if (!create) return undefined;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ]);
  await db.put('meta', { key: DEVICE_KEY, value: key });
  return key;
}

export async function saveSourceLoginSecret(
  sourceId: string,
  secret: SourceLoginSecret,
): Promise<void> {
  const key = await deviceKey(true);
  if (!key) return;
  const blob = await encryptJson(secret, key);
  const db = await getDb();
  await db.put('meta', { key: secretKey(sourceId), value: blob });
}

export async function loadSourceLoginSecret(
  sourceId: string,
): Promise<SourceLoginSecret | undefined> {
  try {
    const db = await getDb();
    const row = await db.get('meta', secretKey(sourceId));
    const blob = row?.value as { ct?: unknown; iv?: unknown } | undefined;
    if (typeof blob?.ct !== 'string' || typeof blob.iv !== 'string') return undefined;
    const key = await deviceKey(false);
    if (!key) return undefined;
    return await decryptJson<SourceLoginSecret>(blob.ct, blob.iv, key);
  } catch {
    return undefined;
  }
}

export async function deleteSourceLoginSecret(sourceId: string): Promise<void> {
  const db = await getDb();
  await db.delete('meta', secretKey(sourceId));
}
